<template>
  <div class="card purchase-register">
    <PageHeader
        title="Purchase Register"
        subtitle="Bill, item and supplier wise purchase analysis"
        @onClickCloseButton="navigateToListPage"
    />
    <div class="register-tabs">
      <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="register-tab-btn"
          :class="{ 'is-active': activeTab === tab.key }"
          @click="onChangeTab(tab.key)"
      >
        <i :class="tab.icon"></i>
        {{ tab.label }}
      </button>
    </div>
    <div class="register-body">
      <BillTab
          v-if="activeTab === 'bill'"
          :business-list="businessList"
          :project-list="projectList"
      />
      <ItemTab
          v-if="activeTab === 'item'"
          :project-list="projectList"
          :home-data="homeData"
      />
      <SupplierTab
          v-if="activeTab === 'supplier'"
          :business-list="businessList"
      />
    </div>
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Loader from '@/components/atom/LoaderComponent'
import PageHeader from '@/components/atom/PageHeader.vue'
import BillTab from '@/components/molecule/company/inventory/purchase-register/BillTab.vue'
import ItemTab from '@/components/molecule/company/inventory/purchase-register/ItemTab.vue'
import SupplierTab from '@/components/molecule/company/inventory/purchase-register/SupplierTab.vue'
import handleDropdowns from '@/services/modules/dropdowns'

const loading = ref(false)
const route = useRoute()
const router = useRouter()
const store = useStore()
const showError = inject('showError')
const { fetchDropdownList } = handleDropdowns()
const businessList = ref([])
const projectList = ref([])
const homeData = ref({})

const tabs = [
  { key: 'bill', label: 'Bill Wise', icon: 'fa-solid fa-file-invoice' },
  { key: 'item', label: 'Item Wise', icon: 'fa-solid fa-boxes-stacked' },
  { key: 'supplier', label: 'Supplier Wise', icon: 'fa-solid fa-truck' }
]

const getInitialTab = () => {
  const isKnownTab = tabs.some(tab => tab.key === route.query.tab)
  if (! isKnownTab) return 'bill'
  return route.query.tab
}

const activeTab = ref(getInitialTab())

const onChangeTab = async (tab) => {
  if (activeTab.value === tab) return
  store.commit('resetPagination')
  const query = Object.assign({}, route.query)
  query.page = 1
  query.tab = tab
  await router.push({ path: route.path, query })
  activeTab.value = tab
}

function navigateToListPage() {
  router.push({ name: 'purchase-list', params: route.params })
}

const getDropdowns = async () => {
  loading.value = true
  try {
    const res = await fetchDropdownList(`?company_id=${route.params.companyId}&is_business=1&is_home=1&is_project=1`)
    if (! res.status || ! res.data) return
    businessList.value = res.data.business_list
    projectList.value = res.data.project_list
    homeData.value = res.data.home_data
  } catch (err) {
    showError('Something went wrong')
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  getDropdowns()
})
</script>

<style scoped>
.purchase-register {
  min-height: calc(100vh - 185px);
}
.register-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  padding: 12px 20px 0;
  border-bottom: 1px solid #eaecf0;
}
.register-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: -1px;
  padding: 10px 14px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: #667085;
  white-space: nowrap;
  cursor: pointer;
  transition: color .15s ease, border-color .15s ease;
}
.register-tab-btn:hover {
  color: #101828;
}
.register-tab-btn.is-active {
  color: #7367f0;
  border-bottom-color: #7367f0;
  font-weight: 600;
}
.register-body {
  padding: 16px 20px 20px;
}
</style>
